import * as yup from 'yup';
import { ExternalLink } from 'react-feather';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Event } from './helpers';
import { TranslatableEditor } from '../../../Translations/Inputs';
import { getServerErrors } from '../../../../common/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import UI from '../../../../common/UI';
import WebsiteInput from '../../../Common/WebsiteInput';
import useEditEventMutation from './useEditEventMutation';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useValidation from '../../../../common/useValidation';

const editEventTermsSchema = yup.object();

export interface EditEventTermsFormProps {
  event: Event;
}

const EditEventTermsForm = ({ event }: EditEventTermsFormProps) => {
  const { t } = useTranslation();

  const getValues = (event: Event) => ({
    type: event.terms_text
      ? 'text' : (event.terms_url ? 'website' : (event.terms ? 'pdf' : 'none')),
    terms: null as any,
    delete_terms: false,
    terms_url: event.terms_url,
    terms_text: event.terms_text,
    terms_text_translations: translationsForAttribute(event.translations, 'terms_text'),
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      terms: {
        terms: form.values.type === 'pdf' ? form.values.terms : null,
        terms_url: form.values.type === 'website' ? form.values.terms_url : null,
        terms_text: form.values.type === 'text' ? form.values.terms_text : null,
        terms_text_translations: form.values.type === 'text' ? form.values.terms_text_translations : null,
      },
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
      setFormKey((key) => key + 1);
    },
  });

  const { valid, errors } = useValidation({
    schema: editEventTermsSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editEvent()} key={formKey}>
      <UI.FormGrid>

        <UI.ServerErrors error={error} sc={{ mb: 4 }} />

        <UI.Div>
          <UI.Legend sc={{ mb: 1 }}>{t('terms_and_conditions')}</UI.Legend>
          <UI.InputDescription>
            {t('terms_and_conditions_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.Div>
          <UI.FormGrid sc={{ gutter: 0.5 }}>
            <UI.Radio
              onChange={() => form.set('type', 'none')}
              checked={form.values.type === 'none'}
              sc={{ box: true }}
            >
              <UI.Strong sc={{ block: true }}>{t('no_terms')}</UI.Strong>
              {t('no_terms_description')}
            </UI.Radio>
            <UI.Radio
              onChange={() => form.set('type', 'pdf')}
              checked={form.values.type === 'pdf'}
              sc={{ box: true }}
            >
              <UI.Strong sc={{ block: true }}>{t('terms_via_pdf')}</UI.Strong>
              {t('terms_via_pdf_description')}
            </UI.Radio>
            {form.values.type === 'pdf' && (
              <UI.InputGroup sc={{ valid: !errors.terms, touched: form.touched.terms }}>
                {(!event.terms || form.values.delete_terms) && (
                  <>
                    <UI.Input {...form.file('terms')} accept="application/pdf" />
                    <UI.ErrorMessages attribute={t('terms')} errors={errors.terms} />
                    {form.values.delete_terms && (
                      <UI.InputDescription sc={{ mt: 1 }}>
                        <UI.A onClick={() => form.set('delete_terms', false)}>
                          {t('restore_pdf')}
                        </UI.A>
                      </UI.InputDescription>
                    )}
                  </>
                )}
                {(event.terms && !form.values.delete_terms) && (
                  <UI.Card sc={{ outline: 'gray.200', padding: 2 }}>
                    <UI.InputDescription>
                      <a href={event.terms.url} target="_blank" rel="noopener noreferrer">
                        {t('view_pdf')}
                        {' '}
                        <UI.Icon>
                          <ExternalLink />
                        </UI.Icon>
                      </a>
                      <UI.Span sc={{ muted: true }}> • </UI.Span>
                      <UI.A onClick={() => form.set('delete_terms', true)}>
                        {t('remove')}
                      </UI.A>
                    </UI.InputDescription>
                  </UI.Card>
                )}
              </UI.InputGroup>
            )}

            <UI.Radio
              onChange={() => form.set('type', 'website')}
              checked={form.values.type === 'website'}
              sc={{ box: true }}
            >
              <UI.Strong sc={{ block: true }}>{t('terms_via_website')}</UI.Strong>
              {t('terms_via_website_description')}
            </UI.Radio>
            {form.values.type === 'website' && (
              <UI.InputGroup sc={{ valid: !errors.terms_url, touched: form.touched.terms_url }}>
                <UI.InputWrapper>
                  <WebsiteInput
                    form={form}
                    formKey="terms_url"
                    id="terms"
                    sc={{ pr: form.values.terms_url && !errors.terms_url ? 11 : undefined }}
                  />
                  {form.values.terms_url && !errors.terms_url && (
                    <UI.Span>
                      <UI.A href={form.values.terms_url} target="_blank" rel="noopener noreferrer">
                        {t('test')}
                        {' '}
                        <UI.Icon>
                          <ExternalLink />
                        </UI.Icon>
                      </UI.A>
                    </UI.Span>
                  )}
                </UI.InputWrapper>
                <UI.ErrorMessages
                  attribute={t('terms_url')}
                  errors={errors.terms_url}
                />
              </UI.InputGroup>
            )}

            <UI.Radio
              onChange={() => form.set('type', 'text')}
              checked={form.values.type === 'text'}
              sc={{ box: true }}
            >
              <UI.Strong sc={{ block: true }}>{t('terms_via_text')}</UI.Strong>
              {t('terms_via_text_description')}
            </UI.Radio>
            {form.values.type === 'text' && (
              <UI.InputGroup sc={{ valid: !errors.terms_text, touched: form.touched.terms_text }}>
                <TranslatableEditor
                  form={form}
                  name="terms_text"
                  defaultLocale={event.locale}
                  title={t('terms_via_text')}
                  attributes={{
                    'aria-label': t('terms_via_text'),
                  }}
                />
                <UI.ErrorMessages
                  attribute={t('terms_via_text')}
                  errors={errors.terms_text}
                />
              </UI.InputGroup>
            )}
          </UI.FormGrid>
        </UI.Div>

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventTermsForm;
